import React, { useState } from 'react';
import { StatsCard } from './shared/StatsCard';
import { Chart } from './shared/Chart';
import { Download, DollarSign, Droplet, Fish, Wallet, AlertCircle, Star, Users, TrendingUp, Heart, Brain, Lightbulb, ArrowUp, ArrowDown, Info } from 'lucide-react';
// Angels data
const angels = [{
  id: 'sophia',
  name: 'Sophia',
  revenue: {
    whales: 125000,
    dolphins: 95000,
    minnows: 45000,
    'non-spenders': 5000,
    'churn-risks': 35000
  },
  algorithmCoverage: 92,
  fanClubPrice: 14.99,
  subscribers: 12500,
  recommendedPrice: 19.99,
  predictedLift: 15,
  attractsSegment: 'whales',
  contentType: 'premium',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80'
}, {
  id: 'harper',
  name: 'Harper',
  revenue: {
    whales: 85000,
    dolphins: 125000,
    minnows: 65000,
    'non-spenders': 8500,
    'churn-risks': 25000
  },
  algorithmCoverage: 88,
  fanClubPrice: 9.99,
  subscribers: 18500,
  recommendedPrice: 14.99,
  predictedLift: 25,
  attractsSegment: 'dolphins',
  contentType: 'interactive',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80'
}, {
  id: 'zoey',
  name: 'Zoey',
  revenue: {
    whales: 45000,
    dolphins: 75000,
    minnows: 120000,
    'non-spenders': 15000,
    'churn-risks': 40000
  },
  algorithmCoverage: 85,
  fanClubPrice: 7.99,
  subscribers: 22500,
  recommendedPrice: 9.99,
  predictedLift: 18,
  attractsSegment: 'minnows',
  contentType: 'casual',
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80'
}, {
  id: 'bella',
  name: 'Bella',
  revenue: {
    whales: 55000,
    dolphins: 85000,
    minnows: 75000,
    'non-spenders': 12000,
    'churn-risks': 45000
  },
  algorithmCoverage: 82,
  fanClubPrice: 9.99,
  subscribers: 16800,
  recommendedPrice: 12.99,
  predictedLift: 22,
  attractsSegment: 'churn-risks',
  contentType: 'personalized',
  image: 'https://images.unsplash.com/photo-1554151228-14d9def656e4?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80'
}, {
  id: 'lana',
  name: 'Lana',
  revenue: {
    whales: 95000,
    dolphins: 65000,
    minnows: 35000,
    'non-spenders': 7500,
    'churn-risks': 30000
  },
  algorithmCoverage: 90,
  fanClubPrice: 12.99,
  subscribers: 14200,
  recommendedPrice: 14.99,
  predictedLift: 12,
  attractsSegment: 'whales',
  contentType: 'premium',
  image: 'https://images.unsplash.com/photo-1593529467220-9d721ceb9a78?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80'
}];
// Predictive suggestions
const predictiveSuggestions = [{
  id: 'suggestion1',
  angel: 'Sophia',
  suggestion: 'Increase fan club price to $19.99',
  impact: 'high',
  reasoning: 'Attracts high percentage of whales (85% algorithm confidence)'
}, {
  id: 'suggestion2',
  angel: 'Harper',
  suggestion: 'Create more interactive content',
  impact: 'medium',
  reasoning: 'Users engage 42% longer with interactive content'
}, {
  id: 'suggestion3',
  angel: 'Zoey',
  suggestion: 'Target more minnows with token-based offerings',
  impact: 'high',
  reasoning: 'Attracts highest percentage of minnows (92% algorithm confidence)'
}, {
  id: 'suggestion4',
  angel: 'Bella',
  suggestion: 'Focus on personalized re-engagement content',
  impact: 'medium',
  reasoning: 'Most effective at recovering churn risk users'
}];
// Chart data for revenue by segment per angel
const revenueBySegmentData = angels.map(angel => ({
  name: angel.name,
  whales: angel.revenue.whales,
  dolphins: angel.revenue.dolphins,
  minnows: angel.revenue.minnows,
  'non-spenders': angel.revenue['non-spenders'],
  'churn-risks': angel.revenue['churn-risks']
}));
// Chart data for algorithm coverage
const algorithmCoverageData = angels.map(angel => ({
  name: angel.name,
  algorithm: angel.algorithmCoverage,
  fallback: 100 - angel.algorithmCoverage
}));
export const AngelStudio: React.FC = () => {
  const [selectedAngel, setSelectedAngel] = useState<string | null>(null);
  const [fanClubPrices, setFanClubPrices] = useState(angels.reduce((acc, angel) => ({
    ...acc,
    [angel.id]: angel.fanClubPrice
  }), {}));
  const handlePriceChange = (angelId: string, price: number) => {
    setFanClubPrices({
      ...fanClubPrices,
      [angelId]: price
    });
  };
  const totalRevenue = angels.reduce((sum, angel) => {
    return sum + Object.values(angel.revenue).reduce((angelSum, revenue) => angelSum + revenue, 0);
  }, 0);
  const totalSubscribers = angels.reduce((sum, angel) => sum + angel.subscribers, 0);
  const avgAlgorithmCoverage = angels.reduce((sum, angel) => sum + angel.algorithmCoverage, 0) / angels.length;
  return <div data-id="element-10304">
      <div className="flex items-center justify-between mb-6" data-id="element-10305">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-10306">
          Angel Studio Integration
        </h2>
        <div className="flex items-center gap-3" data-id="element-10307">
          <button className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-10308">
            <Download size={16} data-id="element-10309" />
            <span data-id="element-10310">Export Angel Data</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-10311">
        <StatsCard title="Total Angel Revenue" value={`$${(totalRevenue / 1000).toFixed(1)}K`} change={15.3} changeLabel="vs last month" icon={<Star size={16} className="text-amber-500" data-id="element-10313" />} className="bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20" data-id="element-10312" />
        <StatsCard title="Total Subscribers" value={totalSubscribers.toLocaleString()} change={8.7} changeLabel="vs last month" icon={<Users size={16} className="text-blue-500" data-id="element-10315" />} className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20" data-id="element-10314" />
        <StatsCard title="Avg. Algorithm Coverage" value={`${avgAlgorithmCoverage.toFixed(1)}%`} change={4.5} changeLabel="vs last month" icon={<Brain size={16} className="text-purple-500" data-id="element-10317" />} className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20" data-id="element-10316" />
        <StatsCard title="Predictive Suggestions" value={predictiveSuggestions.length.toString()} change={2} changeLabel="new suggestions" icon={<Lightbulb size={16} className="text-yellow-500" data-id="element-10319" />} className="bg-gradient-to-br from-white to-yellow-50 dark:from-gray-800 dark:to-yellow-900/20" data-id="element-10318" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-10320">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-10321">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10322">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10323">
              <TrendingUp size={18} className="text-blue-500 mr-2" data-id="element-10324" />
              Angel Revenue by Segment
            </h3>
          </div>
          <div className="p-6" data-id="element-10325">
            <div className="h-80" data-id="element-10326">
              <Chart title="" data={revenueBySegmentData} type="bar" height={300} yAxisLabel="Revenue ($)" data-id="element-10327" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-10328">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10329">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10330">
              <Brain size={18} className="text-purple-500 mr-2" data-id="element-10331" />
              Algorithm Coverage by Angel
            </h3>
          </div>
          <div className="p-6" data-id="element-10332">
            <div className="h-80" data-id="element-10333">
              <Chart title="" data={algorithmCoverageData} type="bar" height={300} yAxisLabel="Coverage (%)" data-id="element-10334" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-10335">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10336">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10337">
            <Star size={18} className="text-amber-500 mr-2" data-id="element-10338" />
            Angel Monetization Configuration
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-10339">
          <table className="w-full" data-id="element-10340">
            <thead data-id="element-10341">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-10342">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10343">
                  Angel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10344">
                  Primary Segment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10345">
                  Content Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10346">
                  Fan Club Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10347">
                  Subscribers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10348">
                  Algorithm Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10349">
                  Recommended Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10350">
                  Predicted Lift
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-10351">
              {angels.map(angel => <tr key={angel.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/60 ${selectedAngel === angel.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} onClick={() => setSelectedAngel(angel.id === selectedAngel ? null : angel.id)} data-id="element-10352">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10353">
                    <div className="flex items-center" data-id="element-10354">
                      <div className="flex-shrink-0 h-10 w-10" data-id="element-10355">
                        <img className="h-10 w-10 rounded-full object-cover" src={angel.image} alt={angel.name} data-id="element-10356" />
                      </div>
                      <div className="ml-3" data-id="element-10357">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-10358">
                          {angel.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10359">
                    <div className="flex items-center" data-id="element-10360">
                      {angel.attractsSegment === 'whales' ? <DollarSign size={16} className="text-emerald-500 mr-2" data-id="element-10361" /> : angel.attractsSegment === 'dolphins' ? <Droplet size={16} className="text-blue-500 mr-2" data-id="element-10362" /> : angel.attractsSegment === 'minnows' ? <Fish size={16} className="text-cyan-500 mr-2" data-id="element-10363" /> : angel.attractsSegment === 'non-spenders' ? <Wallet size={16} className="text-gray-500 mr-2" data-id="element-10364" /> : <AlertCircle size={16} className="text-red-500 mr-2" data-id="element-10365" />}
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-10366">
                        {angel.attractsSegment.charAt(0).toUpperCase() + angel.attractsSegment.slice(1).replace('-', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10367">
                    {angel.contentType.charAt(0).toUpperCase() + angel.contentType.slice(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10368">
                    <input type="number" min="0.99" step="0.01" value={fanClubPrices[angel.id]} onChange={e => handlePriceChange(angel.id, parseFloat(e.target.value))} className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-10369" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10370">
                    {angel.subscribers.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10371">
                    <div className="flex items-center" data-id="element-10372">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-10373">
                        <div className="h-full bg-purple-500 rounded-full" style={{
                      width: `${angel.algorithmCoverage}%`
                    }} data-id="element-10374"></div>
                      </div>
                      <span className="ml-2" data-id="element-10375">{angel.algorithmCoverage}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-10376">
                    <div className="flex items-center" data-id="element-10377">
                      <span data-id="element-10378">${angel.recommendedPrice.toFixed(2)}</span>
                      {angel.recommendedPrice > angel.fanClubPrice ? <ArrowUp size={16} className="ml-2 text-green-500" data-id="element-10379" /> : angel.recommendedPrice < angel.fanClubPrice ? <ArrowDown size={16} className="ml-2 text-red-500" data-id="element-10380" /> : null}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400" data-id="element-10381">
                    +{angel.predictedLift}%
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-10382">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10383">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10384">
            <Lightbulb size={18} className="text-yellow-500 mr-2" data-id="element-10385" />
            Predictive Suggestions
          </h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10386">
          {predictiveSuggestions.map(suggestion => <div key={suggestion.id} className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4" data-id="element-10387">
              <div className="flex items-start" data-id="element-10388">
                <div className="flex-shrink-0 mt-1" data-id="element-10389">
                  <Lightbulb size={18} className="text-yellow-500" data-id="element-10390" />
                </div>
                <div className="ml-3" data-id="element-10391">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-10392">
                    {suggestion.angel}: {suggestion.suggestion}
                  </h4>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-10393">
                    {suggestion.reasoning}
                  </p>
                  <div className="mt-2 flex items-center" data-id="element-10394">
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-10395">
                      Impact:
                    </span>
                    <span className={`ml-1 text-xs font-medium ${suggestion.impact === 'high' ? 'text-green-600 dark:text-green-400' : suggestion.impact === 'medium' ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} data-id="element-10396">
                      {suggestion.impact.charAt(0).toUpperCase() + suggestion.impact.slice(1)}
                    </span>
                    <button className="ml-auto text-xs text-blue-600 dark:text-blue-400 font-medium hover:underline" data-id="element-10397">
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-10398">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-10399">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10400">
            <Info size={18} className="text-blue-500 mr-2" data-id="element-10401" />
            Segment-Based Angel Optimization
          </h3>
        </div>
        <div className="p-6" data-id="element-10402">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10403">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4" data-id="element-10404">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-10405">
                <Brain size={16} className="text-blue-500 mr-2" data-id="element-10406" />
                Algorithmic Angel Matching
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-10407">
                The system analyzes user behavior to match them with the most
                compatible Angels:
              </p>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 list-disc pl-5" data-id="element-10408">
                <li data-id="element-10409">Chat history and content preferences</li>
                <li data-id="element-10410">Spending patterns and price sensitivity</li>
                <li data-id="element-10411">Engagement with similar Angels</li>
                <li data-id="element-10412">Ritual completion patterns</li>
                <li data-id="element-10413">Visual and personality preferences</li>
              </ul>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4" data-id="element-10414">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-10415">
                <Heart size={16} className="text-purple-500 mr-2" data-id="element-10416" />
                Segment-Specific Angel Strategies
              </h4>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-10417">
                <li className="flex items-start" data-id="element-10418">
                  <DollarSign size={14} className="text-emerald-500 mr-2 flex-shrink-0 mt-1" data-id="element-10419" />
                  <div data-id="element-10420">
                    <strong data-id="element-10421">Whales:</strong> Premium, exclusive content with
                    high personalization. Higher price points ($14.99-$29.99)
                    with VIP perks.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-10422">
                  <Droplet size={14} className="text-blue-500 mr-2 flex-shrink-0 mt-1" data-id="element-10423" />
                  <div data-id="element-10424">
                    <strong data-id="element-10425">Dolphins:</strong> Interactive content with moderate
                    personalization. Mid-range price points ($9.99-$14.99) with
                    regular new content.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-10426">
                  <Fish size={14} className="text-cyan-500 mr-2 flex-shrink-0 mt-1" data-id="element-10427" />
                  <div data-id="element-10428">
                    <strong data-id="element-10429">Minnows:</strong> Casual, accessible content with
                    token-based extras. Lower price points ($4.99-$9.99) with
                    free content teasers.
                  </div>
                </li>
                <li className="flex items-start" data-id="element-10430">
                  <AlertCircle size={14} className="text-red-500 mr-2 flex-shrink-0 mt-1" data-id="element-10431" />
                  <div data-id="element-10432">
                    <strong data-id="element-10433">Churn Risks:</strong> Highly personalized
                    re-engagement content. Special offers and exclusive "come
                    back" perks.
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg" data-id="element-10434">
            <div className="flex items-start" data-id="element-10435">
              <div className="flex-shrink-0 mt-1" data-id="element-10436">
                <TrendingUp size={18} className="text-green-500" data-id="element-10437" />
              </div>
              <div className="ml-3" data-id="element-10438">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-10439">
                  Algorithm vs Fallback Performance
                </h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-10440">
                  When algorithm confidence is high (above 85%), Angel matching
                  shows a{' '}
                  <span className="font-medium text-green-600 dark:text-green-400" data-id="element-10441">
                    42% higher engagement rate
                  </span>{' '}
                  and{' '}
                  <span className="font-medium text-green-600 dark:text-green-400" data-id="element-10442">
                    38% higher revenue per user
                  </span>{' '}
                  compared to fallback persona-based matching.
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-10443">
                  <strong data-id="element-10444">Recommendation:</strong> Continue increasing algorithm
                  coverage for all Angels, with priority on those that attract
                  Whales and Dolphins.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};